/**
 * Servicio de API para Supabase - ControlCash
 * Todas las funciones usan auth.uid() vía RLS; userId se pasa para consistencia.
 */
import { supabase } from "../lib/supabase";

const mapMonthFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        year: row.year,
        monthNumber: row.month,
      }
    : null;

const mapIncomeFromDb = (row) =>
  row
    ? {
        id: row.id,
        monthId: row.month_id,
        name: row.name,
        amount: parseFloat(row.amount),
      }
    : null;

const mapExpenseFromDb = (row) =>
  row
    ? {
        id: row.id,
        monthId: row.month_id,
        name: row.name,
        amount: parseFloat(row.amount),
        category: row.category,
        status: row.status || "PENDING",
        dueDay: row.due_day,
        isFromRecurring: row.is_from_recurring ?? false,
        recurringTemplateId: row.recurring_template_id,
        remindDaysBefore: row.remind_days_before,
        date: row.date,
        paidAt: row.paid_at,
      }
    : null;

const mapTemplateFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        amount: parseFloat(row.amount),
        category: row.category,
        dueDay: row.due_day,
        active: row.active ?? true,
        remindDaysBefore: row.remind_days_before,
      }
    : null;

const mapIncomeTemplateFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        amount: parseFloat(row.amount),
        active: row.active ?? true,
      }
    : null;

const mapGoalFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        targetAmount: parseFloat(row.target_amount),
        targetDate: row.target_date,
        status: row.status || "ACTIVE",
      }
    : null;

const mapContributionFromDb = (row) =>
  row
    ? {
        id: row.id,
        goalId: row.goal_id,
        monthId: row.month_id,
        amount: parseFloat(row.amount),
        date: row.date,
        note: row.note,
      }
    : null;

const mapCategoryFromDb = (row) =>
  row ? { id: row.id, name: row.name } : null;

// --- User settings ---
export async function fetchUserSettings(userId) {
  const { data, error } = await supabase
    .from("user_settings")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function upsertUserSettings(userId, { currency, usdToPenRate, exchangeRates, showAmounts, enabledCurrencies }) {
  const penRate = exchangeRates?.PEN ?? usdToPenRate ?? 3.75;
  const row = {
    user_id: userId,
    currency: currency ?? "PEN",
    usd_to_pen_rate: parseFloat(penRate ?? 3.75),
    show_amounts: showAmounts ?? true,
    updated_at: new Date().toISOString(),
  };
  if (enabledCurrencies !== undefined && Array.isArray(enabledCurrencies)) {
    row.enabled_currencies = enabledCurrencies;
  }
  if (exchangeRates !== undefined && typeof exchangeRates === "object") {
    row.exchange_rates = exchangeRates;
  }
  let { data, error } = await supabase
    .from("user_settings")
    .upsert(row, { onConflict: "user_id" })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// --- Months ---
export async function fetchMonths(userId) {
  const { data, error } = await supabase
    .from("months")
    .select("*")
    .eq("user_id", userId)
    .order("year", { ascending: false })
    .order("month", { ascending: false });

  if (error) throw error;
  return (data || []).map(mapMonthFromDb);
}

export async function createMonth(userId, { name, monthNumber, year }) {
  const { data, error } = await supabase
    .from("months")
    .insert({
      user_id: userId,
      name,
      month: monthNumber,
      year,
    })
    .select()
    .single();

  if (error) throw error;
  return mapMonthFromDb(data);
}

export async function deleteMonth(userId, monthId) {
  const { error } = await supabase.from("months").delete().eq("id", monthId).eq("user_id", userId);
  if (error) throw error;
}

// --- Incomes ---
export async function fetchIncomes(userId) {
  const { data, error } = await supabase.from("incomes").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapIncomeFromDb);
}

export async function addIncome(userId, { monthId, name, amount }) {
  const { data, error } = await supabase
    .from("incomes")
    .insert({
      user_id: userId,
      month_id: monthId,
      name,
      amount,
    })
    .select()
    .single();

  if (error) throw error;
  return mapIncomeFromDb(data);
}

export async function updateIncome(userId, { incomeId, name, amount }) {
  const { data, error } = await supabase
    .from("incomes")
    .update({ name, amount })
    .eq("id", incomeId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapIncomeFromDb(data);
}

export async function deleteIncome(userId, incomeId) {
  const { error } = await supabase.from("incomes").delete().eq("id", incomeId).eq("user_id", userId);
  if (error) throw error;
}

// --- Expenses ---
export async function fetchExpenses(userId) {
  const { data, error } = await supabase.from("expenses").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapExpenseFromDb);
}

function expenseToRow(payload) {
  const row = {
    user_id: null,
    month_id: payload.monthId,
    name: payload.name,
    amount: payload.amount,
    category: payload.category ?? null,
    status: payload.status ?? "PENDING",
    due_day: payload.dueDay ?? null,
    is_from_recurring: payload.isFromRecurring ?? false,
    recurring_template_id: payload.recurringTemplateId ?? null,
    remind_days_before: payload.remindDaysBefore ?? null,
    date: payload.date ?? null,
    paid_at: payload.paidAt ?? null,
  };
  return row;
}

export async function addExpense(userId, payload) {
  const row = expenseToRow(payload);
  row.user_id = userId;

  const { data, error } = await supabase.from("expenses").insert(row).select().single();
  if (error) throw error;
  return mapExpenseFromDb(data);
}

export async function updateExpense(userId, expenseId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.status !== undefined) row.status = patch.status;
  if (patch.dueDay !== undefined) row.due_day = patch.dueDay;
  if (patch.isFromRecurring !== undefined) row.is_from_recurring = patch.isFromRecurring;
  if (patch.recurringTemplateId !== undefined) row.recurring_template_id = patch.recurringTemplateId;
  if (patch.remindDaysBefore !== undefined) row.remind_days_before = patch.remindDaysBefore;
  if (patch.date !== undefined) row.date = patch.date;
  if (patch.paidAt !== undefined) row.paid_at = patch.paidAt;

  const { data, error } = await supabase
    .from("expenses")
    .update(row)
    .eq("id", expenseId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapExpenseFromDb(data);
}

export async function deleteExpense(userId, expenseId) {
  const { error } = await supabase.from("expenses").delete().eq("id", expenseId).eq("user_id", userId);
  if (error) throw error;
}

export async function toggleExpensePaid(userId, expenseId) {
  const { data: current } = await supabase.from("expenses").select("status, paid_at").eq("id", expenseId).eq("user_id", userId).single();
  if (!current) throw new Error("Gasto no encontrado");
  const newStatus = current.status === "PENDING" ? "PAID" : "PENDING";
  const paidAt = newStatus === "PAID" ? new Date().toISOString() : null;
  const { data, error } = await supabase
    .from("expenses")
    .update({ status: newStatus, paid_at: paidAt })
    .eq("id", expenseId)
    .eq("user_id", userId)
    .select()
    .single();
  if (error) throw error;
  return mapExpenseFromDb(data);
}

// --- Recurring templates ---
export async function fetchRecurringTemplates(userId) {
  const { data, error } = await supabase.from("recurring_templates").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapTemplateFromDb);
}

export async function createRecurringTemplate(userId, { name, amount, category, dueDay, active, remindDaysBefore }) {
  const { data, error } = await supabase
    .from("recurring_templates")
    .insert({
      user_id: userId,
      name,
      amount: amount ?? 0,
      category: category ?? null,
      due_day: dueDay ?? null,
      active: active ?? true,
      remind_days_before: remindDaysBefore ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapTemplateFromDb(data);
}

export async function updateRecurringTemplate(userId, templateId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.dueDay !== undefined) row.due_day = patch.dueDay;
  if (patch.active !== undefined) row.active = patch.active;
  if (patch.remindDaysBefore !== undefined) row.remind_days_before = patch.remindDaysBefore;

  const { data, error } = await supabase
    .from("recurring_templates")
    .update(row)
    .eq("id", templateId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapTemplateFromDb(data);
}

export async function deleteRecurringTemplate(userId, templateId) {
  const { error } = await supabase.from("recurring_templates").delete().eq("id", templateId).eq("user_id", userId);
  if (error) throw error;
}

// --- Recurring income templates ---
export async function fetchRecurringIncomeTemplates(userId) {
  const { data, error } = await supabase.from("recurring_income_templates").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapIncomeTemplateFromDb);
}

export async function createRecurringIncomeTemplate(userId, { name, amount, active }) {
  const { data, error } = await supabase
    .from("recurring_income_templates")
    .insert({
      user_id: userId,
      name,
      amount: amount ?? 0,
      active: active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return mapIncomeTemplateFromDb(data);
}

export async function updateRecurringIncomeTemplate(userId, templateId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.active !== undefined) row.active = patch.active;

  const { data, error } = await supabase
    .from("recurring_income_templates")
    .update(row)
    .eq("id", templateId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapIncomeTemplateFromDb(data);
}

export async function deleteRecurringIncomeTemplate(userId, templateId) {
  const { error } = await supabase.from("recurring_income_templates").delete().eq("id", templateId).eq("user_id", userId);
  if (error) throw error;
}

// --- Goals ---
export async function fetchGoals(userId) {
  const { data, error } = await supabase.from("goals").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapGoalFromDb);
}

export async function createGoal(userId, { name, targetAmount, targetDate, status }) {
  const { data, error } = await supabase
    .from("goals")
    .insert({
      user_id: userId,
      name,
      target_amount: targetAmount ?? 0,
      target_date: targetDate ?? null,
      status: status ?? "ACTIVE",
    })
    .select()
    .single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function updateGoal(userId, goalId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.targetAmount !== undefined) row.target_amount = patch.targetAmount;
  if (patch.targetDate !== undefined) row.target_date = patch.targetDate;
  if (patch.status !== undefined) row.status = patch.status;

  const { data, error } = await supabase
    .from("goals")
    .update(row)
    .eq("id", goalId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function deleteGoal(userId, goalId) {
  const { error } = await supabase.from("goals").delete().eq("id", goalId).eq("user_id", userId);
  if (error) throw error;
}

// --- Contributions ---
export async function fetchContributions(userId) {
  const { data, error } = await supabase.from("contributions").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapContributionFromDb);
}

export async function addContribution(userId, { goalId, monthId, amount, date, note }) {
  const { data, error } = await supabase
    .from("contributions")
    .insert({
      user_id: userId,
      goal_id: goalId,
      month_id: monthId ?? null,
      amount,
      date: date ?? new Date().toISOString().split("T")[0],
      note: note ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapContributionFromDb(data);
}

export async function deleteContribution(userId, contributionId) {
  const { error } = await supabase.from("contributions").delete().eq("id", contributionId).eq("user_id", userId);
  if (error) throw error;
}

// --- Custom categories ---
export async function fetchCustomCategories(userId) {
  const { data, error } = await supabase.from("custom_categories").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapCategoryFromDb);
}

export async function createCustomCategory(userId, { id, name }) {
  const catId = id || `cat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const { data, error } = await supabase
    .from("custom_categories")
    .insert({
      id: catId,
      user_id: userId,
      name,
    })
    .select()
    .single();

  if (error) throw error;
  return mapCategoryFromDb(data);
}

export async function updateCustomCategory(userId, catId, { name }) {
  const { data, error } = await supabase
    .from("custom_categories")
    .update({ name })
    .eq("id", catId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapCategoryFromDb(data);
}

export async function deleteCustomCategory(userId, catId) {
  const { error } = await supabase.from("custom_categories").delete().eq("id", catId).eq("user_id", userId);
  if (error) throw error;
}
